<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div
      v-if="loanAccountSuccessCount.length || loanAccountFailed.length"
      class="m-2"
    >
      <b-row>
        <b-col
          v-if="loanAccountSuccessCount"
          md="6"
          class="mb-2"
        >
          <b-alert
            show
            variant="primary"
            class="p-2"
          >
            {{ loanAccountSuccessCount }}
          </b-alert>
        </b-col>
        <b-col
          v-if="loanAccountFailed.length"
          md="6"
          class="mb-2"
        >
          <div>
            <b-button variant="danger">
              {{ $t('FAILED LISTS') }}
            </b-button>
          </div>
          <b-table
            ref="loanAccountFailedReport"
            :items="loanAccountFailed"
            responsive
            :fields="translatedFailedHeaders"
            primary-key="id"
            show-empty
            :empty-text="$t('No records available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          />
        </b-col>
      </b-row>
    </div>

    <div class="m-2">
      <b-row>
        <b-col
          md="5"
          class="mb-2"
        >
          <validation-observer ref="dataMigrationLoanAccountRules">
            <b-form>
              <b-col
                md="12"
                class="mb-2"
              >
                <div class="mb-1">
                  {{ $t('Select file (.xlsx format only)') }}
                </div>
                <b-form-file
                  id="file-name"
                  v-model="hasFileData"
                  :label="$t('Select file (.xlsx format only)')"
                  :state="Boolean(hasFileData)"
                  :placeholder="$t('Choose a file or drop it here...')"
                  :drop-placeholder="$t('Drop file here...')"
                  error-name="file"
                  rules="required"
                  accept=".xlsx"
                  @change="onChangeXLSX($event)"
                />
              </b-col>
            </b-form>
          </validation-observer>
        </b-col>

        <b-col md="7">
          <b-row>
            <b-col
              md="3"
              class="mt-2"
            >
              <Button
                v-if="getUserPermission('BATCH_LOAN_UPLOAD') && parseExcelFile.length"
                :label="$t('Next >')"
                size="md"
                :hidden="isNextValidateHidden"
                :loading="isLoading"
                @click="handleNextValidate"
              />
              <Button
                v-if="getUserPermission('BATCH_LOAN_UPLOAD') && !errorMessages.length"
                :label="$t('Create Loan Account')"
                size="md"
                :hidden="isCreateBatchHidden"
                :loading="isLoading"
                :disabled="isDisabled"
                @click="handleCreate"
              />
            </b-col>
            <b-col
              md="4"
              class="mt-2"
            >
              <SwitchButton
                v-model="shouldCreateSchedules"
                :label="$t('Should Create Schedules?')"
                name="create-schedules"
              />
            </b-col>
            <b-col
              md="5"
              class="mt-2"
            >
              <Button
                class="ml-2"
                align="end"
                :label="$t('Download Loan Account Migration Format')"
                size="md"
                icon="DownloadIcon"
                show-icon
                :loading="isLoading2"
                :disabled="isDisabled2"
                @click="handleDownloadSubmit"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <b-table
      v-if="errorMessages.length"
      ref="batchloanAccountSuccessCountReport"
      :items="errorMessages"
      responsive
      :fields="errorHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    />
    <p
      v-if="totalSheetRows"
      class="p-2"
      style="font-size: 18px;"
    >
      {{ $t('There are total of') }} ({{ totalSheetRows }}) {{ $t('Records fetched') }}
    </p>
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BForm,
  BTable,
  BFormFile,
  BButton,
  BAlert,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'

// import Papa from 'papaparse'
import XLSX from 'xlsx'
import Button from '../../../components/button/Button.vue'
import {
  batchAccountFormat,
  tableLessHeaders,
  tableAllHeaders,
} from '../../../../helpers/data'
import { toKobo } from '@/helpers/formatAmount'
import { toYearWithDate } from '../../../../helpers/formatDate'
import SwitchButton from '../../../components/switch/Switch.vue'

export default {
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BForm,
    BFormFile,
    ValidationObserver,
    Button,
    BButton,
    BAlert,
    SwitchButton,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      totalSheetRows: '',
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      validatedExcelFileData: [],
      sortOrders: {},
      accountFormat: batchAccountFormat,
      successHeaders: tableAllHeaders,
      failedHeaders: tableLessHeaders,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'oldCustomerID', sortable: true },
        { key: 'newCustomerID', sortable: true },
        { key: 'oldLoanAccountNumber', sortable: true },
        { key: 'OldRepaymentNonNuban', sortable: true, label: 'Old Loan Repayment Account Number (NON NUBAN)' },
        { key: 'oldRepaymentAccountNumber', sortable: true, label: 'Old Repayment Account Number (NUBAN)' },
        { key: 'newRepaymentAccountNumber', sortable: true },
        { key: 'productCode', sortable: true },
        { key: 'branchCode', sortable: true },
        { key: 'accountOfficerCode', sortable: true },
        { key: 'gender', sortable: true },
        { key: 'lendingModel', sortable: true },
        { key: 'economicSector', sortable: true },
        { key: 'guarantorID1', sortable: true },
        { key: 'guarantorID2', sortable: true },
        { key: 'penaltyGracePeriodInDays', sortable: true, label: 'Moratorium (In days)' },
        { key: 'loanAmountDisbursed', sortable: true },
        { key: 'applyCollateralPledge' },
        { key: 'collateralType', sortable: true },
        { key: 'collateralValuation', sortable: true },
        { key: 'collateralDescription', sortable: true },
        { key: 'disbursementDate', sortable: true, label: 'Start Date' },
        { key: 'maturityDate', sortable: true },
        { key: 'loanTenure', sortable: true },
        { key: 'principalRepaymentFrequency', sortable: true },
        { key: 'applicableInterestRate', sortable: true },
        { key: 'interestRepaymentFrequency', sortable: true },
        { key: 'interestAccrualMode', sortable: true },
        { key: 'principalNotYetDue', sortable: true },
        { key: 'principalDueAndUnpaid', sortable: true },
        { key: 'interestDueAndUnpaid', sortable: true },
        { key: 'dueAndUnpaidLoanFees', sortable: true },
        { key: 'dueAndUnpaidLoanPenalty', sortable: true },
        { key: 'holdFundSecurityDeposit', sortable: true },
        { key: 'securityDepositAmountWithheld', sortable: true },
      ],
      required,
      isDisabled: false,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,
      isCreateBatchHidden: true,
      isNextValidateHidden: false,
      loanAccountSuccessCount: [],
      loanAccountFailed: [],
      sortDesc: false,
      sortBy: 'lastName',
      errorHeaders: [
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'message', sortable: true, label: 'Error Messages' },
      ],
      errorMessages: [],
      shouldCreateSchedules: false,
    }
  },
  computed: {
    translatedFailedHeaders() {
      return this.failedHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  methods: {
    ...mapActions({
      migrateLoanAccountCreation: 'batch/migrateLoanAccountCreation',
      downloadAccountFormat: 'batch/downloadAccountFormat',
      validateLoanAccountDataMigration:
        'batch/validateLoanAccountDataMigration',
    }),
    async handleCreate() {
      this.$refs.dataMigrationLoanAccountRules.validate().then(success => {
        if (success) {
          this.confirm(this.migrateLoanAccountAction, {
            text: this.$t('Are you sure you want to Create Batch Accounts?'),
          })
        }
      })
    },
    async migrateLoanAccountAction() {
      try {
        this.loanAccountSuccessCount = ''
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          oldCustomerID: item.oldCustomerID
            ? item.oldCustomerID.toString()
            : null,
          newCustomerID: item.newCustomerID
            ? item.newCustomerID.toString()
            : null,
          oldLoanAccountNumber: item.oldLoanAccountNumber
            ? item.oldLoanAccountNumber.toString()
            : null,
          OldRepaymentNonNuban: item.OldRepaymentNonNuban
            ? item.OldRepaymentNonNuban.toString()
            : null,
          oldRepaymentAccountNumber: item.oldRepaymentAccountNumber
            ? item.oldRepaymentAccountNumber.toString()
            : null,
          newRepaymentAccountNumber: item.newRepaymentAccountNumber
            ? item.newRepaymentAccountNumber.toString()
            : null,
          productCode: item.productCode ? item.productCode.toString() : null,
          branchCode: item.branchCode ? item.branchCode.toString() : null,
          accountOfficerCode: item.accountOfficerCode
            ? item.accountOfficerCode.toString()
            : null,
          gender: item.gender ? item.gender.toString() : null,
          lendingModel: item.lendingModel ? Number(item.lendingModel) : null,
          economicSector: item.economicSector
            ? Number(item.economicSector)
            : null,
          guarantorID1: item.guarantorID1 ? item.guarantorID1.toString() : null,
          guarantorID2: item.guarantorID2 ? item.guarantorID2.toString() : null,
          penaltyGracePeriodInDays: item.penaltyGracePeriodInDays
            ? Number(item.penaltyGracePeriodInDays)
            : null,
          loanAmountDisbursed: item.loanAmountDisbursed ? Number(toKobo(item.loanAmountDisbursed).toFixed()) : null,
          applyCollateralPledge: Boolean(item.applyCollateralPledge),
          collateralType: item.collateralType
            ? Number(item.collateralType)
            : null,
          collateralValuation: item.collateralValuation
            ? Number(item.collateralValuation)
            : null,
          collateralDescription: item.collateralDescription
            ? item.collateralDescription.toString()
            : null,
          disbursementDate: item.disbursementDate
            ? toYearWithDate(item.disbursementDate)
            : null,
          maturityDate: item.maturityDate
            ? toYearWithDate(item.maturityDate)
            : null,
          loanTenure: item.loanTenure ? Number(item.loanTenure) : null,
          principalRepaymentFrequency: item.principalRepaymentFrequency
            ? Number(item.principalRepaymentFrequency)
            : null,
          applicableInterestRate: item.applicableInterestRate
            ? Number(item.applicableInterestRate)
            : null,
          interestRepaymentFrequency: item.interestRepaymentFrequency
            ? Number(item.interestRepaymentFrequency)
            : null,
          interestAccrualMode: item.interestAccrualMode
            ? Number(item.interestAccrualMode)
            : null,
          principalNotYetDue: item.principalNotYetDue
            ? Number(toKobo(item.principalNotYetDue).toFixed())
            : null,
          principalDueAndUnpaid: item.principalDueAndUnpaid
            ? Number(toKobo(item.principalDueAndUnpaid).toFixed())
            : null,
          interestDueAndUnpaid: item.interestDueAndUnpaid
            ? Number(toKobo(item.interestDueAndUnpaid).toFixed())
            : null,
          dueAndUnpaidLoanFees: item.dueAndUnpaidLoanFees
            ? Number(toKobo(item.dueAndUnpaidLoanFees).toFixed())
            : null,
          dueAndUnpaidLoanPenalty: item.dueAndUnpaidLoanPenalty
            ? Number(toKobo(item.dueAndUnpaidLoanPenalty).toFixed())
            : null,
          holdFundSecurityDeposit: Boolean(item.holdFundSecurityDeposit),
          securityDepositAmountWithheld: item.securityDepositAmountWithheld
            ? Number(toKobo(item.securityDepositAmountWithheld).toFixed())
            : null,
        }))
        const setFormData = {
          shouldCreateSchedules: this.shouldCreateSchedules,
          loans: [
            ...formData,
          ],
        }
        const response = await this.migrateLoanAccountCreation(setFormData)
        const { status, data } = response
        if (status === 200) {
          this.hasFileData = []
          this.parseExcelFile = []
          if (data.successCount > 1) {
            const pluralText = this.$t('Loan Accounts was successfully created')
            this.loanAccountSuccessCount = `(${data.successCount}) ${pluralText}`
          } else {
            const singularText = this.$t('Loan Account was successfully created')
            this.loanAccountSuccessCount = `(${data.successCount}) ${singularText}`
          }
          this.loanAccountFailed = data.failed
          this.isDisabled = false
          this.isLoading = false
        }
      } catch (error) {
        const reasonText = this.$t('Your request to create Batch Account failed. Reason:')
        error.response.data.errors.forEach(responseError => {
          this.alertError({
            text: `${reasonText}: ${responseError.errorMessage}`,
          })
        })
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    onChangeXLSX(event) {
      this.errorMessages = []
      this.isCreateBatchHidden = true
      this.fileData = event.target.files ? event.target.files[0] : null
      if (this.fileData) {
        const vm = this
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, {
            type: 'binary',
            cellDates: true,
            dateNF: 'dd/mm/yyyy;@',
          })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rowObject = XLSX.utils.sheet_to_row_object_array(worksheet, {
            raw: false,
          })
          vm.totalSheetRows = rowObject.length
          vm.parseExcelFile = JSON.parse(JSON.stringify(rowObject)).map(
            items => ({
              oldCustomerID: items['Old Customer ID'],
              newCustomerID: items['New Customer ID'],
              oldLoanAccountNumber: items['Old Loan Account Number'],
              OldRepaymentNonNuban: items['Old Loan Repayment Account Number (NON NUBAN)'],
              oldRepaymentAccountNumber: items['Old Repayment Account Number (NUBAN)'],
              newRepaymentAccountNumber: items['New Repayment Account Number'],
              productCode: items['Product Code'],
              branchCode: items['Branch Code'],
              accountOfficerCode: items['Account Officer Code'],
              gender: items['Gender '],
              lendingModel: items['Lending Model'],
              economicSector: items['Economic Sector'],
              guarantorID1: items['Guarantor 1 New ID'],
              guarantorID2: items['Guarantor 2 New ID'],
              penaltyGracePeriodInDays: items['Moratorium (In days)'],
              loanAmountDisbursed: items['Loan Amount Disbursed'],
              applyCollateralPledge: items['Apply Collateral Pledge'],
              collateralType: items['Collateral Type'],
              collateralValuation: items['Collateral Valuation'],
              collateralDescription: items['Collateral Description'],
              disbursementDate: items['Start Date'],
              maturityDate: items['Maturity Date'],
              loanTenure: items['Loan Tenure'],
              principalRepaymentFrequency:
                items['Principal Repayment Frequency'],
              applicableInterestRate: items['Loan Interest Rate'],
              interestRepaymentFrequency: items['Interest Repayment Frequency'],
              interestAccrualMode: items['Interest Accrual Mode'],
              principalNotYetDue: items['Principal Not Yet Due'],
              principalDueAndUnpaid: items['Principal Due & Unpaid'],
              interestDueAndUnpaid: items['Interest Due & Unpaid'],
              dueAndUnpaidLoanFees: items['Due & Unpaid Loan Fees'],
              dueAndUnpaidLoanPenalty: items['Due & Unpaid Loan Penalty'],
              holdFundSecurityDeposit: items['Hold Fund Security Deposit'],
              securityDepositAmountWithheld:
                items['Security Deposit Amt. Withheld'],
            }),
          )
        }
        reader.readAsBinaryString(this.fileData)
      }
    },
    async handleNextValidate() {
      try {
        this.isLoading = true
        this.hasFileData = []
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          oldCustomerID: item.oldCustomerID
            ? item.oldCustomerID.toString()
            : null,
          newCustomerID: item.newCustomerID
            ? item.newCustomerID.toString()
            : null,
          oldLoanAccountNumber: item.oldLoanAccountNumber
            ? item.oldLoanAccountNumber.toString()
            : null,
          OldRepaymentNonNuban: item.OldRepaymentNonNuban
            ? item.OldRepaymentNonNuban.toString()
            : null,
          oldRepaymentAccountNumber: item.oldRepaymentAccountNumber
            ? item.oldRepaymentAccountNumber.toString()
            : null,
          newRepaymentAccountNumber: item.newRepaymentAccountNumber
            ? item.newRepaymentAccountNumber.toString()
            : null,
          productCode: item.productCode ? item.productCode.toString() : null,
          branchCode: item.branchCode ? item.branchCode.toString() : null,
          accountOfficerCode: item.accountOfficerCode
            ? item.accountOfficerCode.toString()
            : null,
          gender: item.gender ? item.gender.toString() : null,
          lendingModel: item.lendingModel ? Number(item.lendingModel) : null,
          economicSector: item.economicSector
            ? Number(item.economicSector)
            : null,
          guarantorID1: item.guarantorID1 ? item.guarantorID1.toString() : null,
          guarantorID2: item.guarantorID2 ? item.guarantorID2.toString() : null,
          penaltyGracePeriodInDays: item.penaltyGracePeriodInDays
            ? Number(item.penaltyGracePeriodInDays)
            : null,
          loanAmountDisbursed: item.loanAmountDisbursed ? Number(toKobo(item.loanAmountDisbursed).toFixed()) : null,
          applyCollateralPledge: Boolean(item.applyCollateralPledge),
          collateralType: item.collateralType
            ? Number(item.collateralType)
            : null,
          collateralValuation: item.collateralValuation
            ? Number(item.collateralValuation)
            : null,
          collateralDescription: item.collateralDescription
            ? item.collateralDescription.toString()
            : null,
          disbursementDate: item.disbursementDate
            ? toYearWithDate(item.disbursementDate)
            : null,
          maturityDate: item.maturityDate
            ? toYearWithDate(item.maturityDate)
            : null,
          loanTenure: item.loanTenure ? Number(item.loanTenure) : null,
          principalRepaymentFrequency: item.principalRepaymentFrequency
            ? Number(item.principalRepaymentFrequency)
            : null,
          applicableInterestRate: item.applicableInterestRate
            ? Number(item.applicableInterestRate)
            : null,
          interestRepaymentFrequency: item.interestRepaymentFrequency
            ? Number(item.interestRepaymentFrequency)
            : null,
          interestAccrualMode: item.interestAccrualMode
            ? Number(item.interestAccrualMode)
            : null,
          principalNotYetDue: item.principalNotYetDue
            ? Number(toKobo(item.principalNotYetDue).toFixed())
            : null,
          principalDueAndUnpaid: item.principalDueAndUnpaid
            ? Number(toKobo(item.principalDueAndUnpaid).toFixed())
            : null,
          interestDueAndUnpaid: item.interestDueAndUnpaid
            ? Number(toKobo(item.interestDueAndUnpaid).toFixed())
            : null,
          dueAndUnpaidLoanFees: item.dueAndUnpaidLoanFees
            ? Number(toKobo(item.dueAndUnpaidLoanFees).toFixed())
            : null,
          dueAndUnpaidLoanPenalty: item.dueAndUnpaidLoanPenalty
            ? Number(toKobo(item.dueAndUnpaidLoanPenalty).toFixed())
            : null,
          holdFundSecurityDeposit: Boolean(item.holdFundSecurityDeposit),
          securityDepositAmountWithheld: item.securityDepositAmountWithheld
            ? Number(toKobo(item.securityDepositAmountWithheld).toFixed())
            : null,
        }))
        const setFormData = {
          shouldCreateSchedules: this.shouldCreateSchedules,
          loans: [
            ...formData,
          ],
        }
        const response = await this.validateLoanAccountDataMigration(setFormData)
        const { status, data } = response
        if (status === 200) {
          this.errorMessages = data.failed
          if (data.successCount > 1) {
            const pluralText = this.$t('Loan Accounts are ready to be created')
            this.loanAccountSuccessCount = `(${data.successCount}) ${pluralText}`
          } else {
            const singularText = this.$t('Loan Account is ready to be created')
            this.loanAccountSuccessCount = `(${data.successCount}) ${singularText}`
          }
          this.isCreateBatchHidden = false
          if (this.errorMessages.length) {
            this.isNextValidateHidden = false
          } else {
            this.isNextValidateHidden = true
            this.alertSuccess({
              text: this.$t('Everything seems good! Continue with loan account data migration'),
            })
          }
          this.isLoading = false
        }
      } catch (error) {
        const errorText = this.$t('Your request to perform Batch Loan Account validation failed. Reason')
        error.response.data.errors.forEach(responseError => {
          this.alertError({
            text: `${errorText}: ${responseError.errorMessage}`,
          })
        })
        this.isCreateBatchHidden = true
        this.isLoading = false
      }
    },
    async handleDownloadSubmit() {
      this.confirm(this.downloadAction, {
        text: this.$t('Are you sure you want to download Loan Account Data Migration Format?'),
      })
    },
    async downloadAction() {
      try {
        this.isLoading2 = true
        this.isDisabled2 = true
        const LOAN_ACCOUNT_MIGRATION_TEMPLATE = 8
        const response = await this.downloadAccountFormat(LOAN_ACCOUNT_MIGRATION_TEMPLATE)
        if (response.status === 200) {
          const downloadAnchor = document.createElement('a')
          downloadAnchor.setAttribute('href', response.data.data)
          const filename = 'Udara360-Loan-Account-Data-Migration.xlsx'
          downloadAnchor.setAttribute('download', filename)
          document.body.appendChild(downloadAnchor)
          downloadAnchor.click()
          document.body.removeChild(downloadAnchor)
          this.alertSuccess({
            text: this.$t('Your request to download Loan Account Data Migration Format was successful'),
          })
        }
      } catch (error) {
        const reasonErrorText = this.$t('Your request to download Loan Account Data Migration Format failed. Reason')
        error.response.data.errors.forEach(responseError => {
          this.alertError({
            text: `${reasonErrorText}: ${responseError.errorMessage}`,
          })
          this.isLoading2 = false
          this.isDisabled2 = false
        })
      } finally {
        this.isLoading2 = false
        this.isDisabled2 = false
      }
    },
  },
}
</script>
